import { rem, SCREEN_BOTTOM_PADDING, ScrollView, SettingsItem, SettingsItemNavigate, Spacer, } from 'design-system';
import useColors from 'hooks/useColors';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useSharedValue } from 'react-native-reanimated';
import { useMarginBottom } from 'hooks/useMarginBottom';
import SectionHeader from 'design-system/SectionHeader';
import IconPlus from 'design-system/icons/IconPlus';
import Squircle from 'design-system/Squircle';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import links from 'utils/links';
import { useAppSelector } from 'store/hooks';
import { IconBank } from 'design-system/icons';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { SpaceItem } from './components';
import { isWeb } from '../../constants';
import { useJoinSpaceModal } from './hooks/useJoinSpaceModal';
import { selectSpaces } from './reducers/selectors';
import { useMySpaceAccounts } from './hooks/useMySpaceAccounts';
import useMoveSpaceModal from './hooks/useMoveSpaceModal';
const ManageSpaces = ({ navigation }) => {
    const spaces = useAppSelector(selectSpaces);
    const styles = useStyles(styleSet);
    const scrollY = useSharedValue(0);
    const colors = useColors();
    const onJoinSpace = useJoinSpaceModal();
    const { accountsCount } = useMySpaceAccounts();
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING);
    const { showMoveSpaceToast, moveAccountsToast } = useMoveSpaceModal();
    const { privateSpace, customSpaces } = useMemo(() => {
        const privateSpace = spaces.find((space) => space.isDefault);
        const customSpaces = spaces.filter((space) => !space.isDefault);
        return { privateSpace, customSpaces };
    }, [spaces]);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Manage spaces',
            headerTintColor: colors.text.primary,
        });
    }, [colors]);
    const onPrivateSpaceAccounts = useCallback(() => {
        if (privateSpace) {
            navigation.navigateDeprecated('PrimarySpaceAccounts', {
                space: privateSpace,
            });
        }
    }, [privateSpace]);
    const onAllAccountsPress = useCallback(() => {
        navigation.navigateDeprecated('AllSpaceAccountsDraggable', {
            onMoveAccounts: showMoveSpaceToast,
        });
    }, [showMoveSpaceToast]);
    const onCustomSpaceAccounts = useCallback((spaceId) => {
        navigation.navigateDeprecated('EditSpace', { id: spaceId });
    }, []);
    const onPressInfo = useCallback(() => {
        links.openKnownLink('spacesHelp');
    }, []);
    if (!privateSpace)
        return null;
    return (<View style={styles.container}>
      <ScrollView secondary scrollY={scrollY} contentContainerStyle={contentContainerStyle}>
        <TouchableBounceCard onPress={onAllAccountsPress} onDark noMarginTop noMarginBottom>
          <View pointerEvents="none" style={styles.accountsBtn}>
            <SettingsItemNavigate titleFirst title="Accounts in Emma" description={`${accountsCount} accounts`} icon={<IconBank gradientKey="lightPurple"/>}/>
          </View>
        </TouchableBounceCard>
        {customSpaces.length > 0 && (<>
            <SectionHeader horizontalMargins largeVerticalMargins>
              Custom spaces
            </SectionHeader>
            <View style={styles.listContainer}>
              <SettingsItem onPress={onJoinSpace} title="Add new space" renderIcon={() => (<View style={styles.addNewSpace}>
                    <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                      <IconPlus gradientKey="ultimate"/>
                    </Squircle>
                  </View>)}/>
              <SpaceItem space={privateSpace} onPress={onPrivateSpaceAccounts}/>
              {customSpaces.map((item) => (<SpaceItem key={item.id} space={item} onPress={onCustomSpaceAccounts}/>))}
            </View>
            <Spacer h={16}/>
            <InfoCard onPress={onPressInfo}>
              <Text TextThin-14 Secondary>
                Adding an account to a custom Space will move it out of the
                Private space. An account can only belong to one Space at a
                time.{'\n'}
                <Text Primary Text-14>
                  Learn more
                </Text>
              </Text>
            </InfoCard>
          </>)}
      </ScrollView>
      {moveAccountsToast()}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        width: '100%',
        flex: 1,
        alignItems: 'stretch',
    },
    listContainer: {
        backgroundColor: colors.cards.onDark,
        marginHorizontal: rem(16),
        borderRadius: rem(16),
        ...(isWeb && {
            padding: 8,
        }),
    },
    accountsBtn: {
        ...(isWeb && {
            paddingHorizontal: 8,
        }),
    },
    addNewSpace: {
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(16),
    },
}));
export default ManageSpaces;
